/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')
    this.personalizationInput = this.form.querySelector('[data-product-personalization-input]')

    this.form.addEventListener('submit', event => this.onSubmit(event))
    this.form.addEventListener('invalid', () => {
      this.form.classList.add('was-validated')
    }, true)

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const selectedOptions = []

    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        selectedOptions.push(elem.value)
      }
    })

    // console.log(selectedOptions)

    const selectedVariant = JSON.parse(this.form.dataset.variants).find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )

    console.log(selectedVariant)

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)

    if (selectedVariant) {
      this.querySelector('[name="id"]').value = selectedVariant.id

      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))
    }
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const compareBadge = this.form.querySelector('.product-sale-badge')
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
      finalPrice.classList.add('product-price-final-sale')
      compareBadge.removeAttribute('hidden')
      if (compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      comparePrice.textContent = ''
      comparePrice.setAttribute('hidden', 'hidden')
      finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
      finalPrice.classList.remove('product-price-final-sale')
      compareBadge.textContent = ''
      compareBadge.setAttribute('hidden', 'hidden')
    }

    if (selectedVariant.available) {
      soldOutBadge.setAttribute('hidden', 'hidden')
    } else {
      soldOutBadge.removeAttribute('hidden')
    }
  }

  adjustButtons (selectedVariant) {
    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn.removeAttribute('disabled')
        this.buyBtn?.removeAttribute('disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled')
        this.buyBtn?.setAttribute('disabled', 'disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.sold_out
      }
    } else {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.buyBtn?.setAttribute('disabled', 'disabled')
      this.atcBtn.innerHTML = window.theme.locales.product.unavailable
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    if (!this.validateForm()) return

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    const formData = new FormData(this.form)
    this.pruneEmptyProperties(formData)
    const cartContainer = document.querySelector('cart-container')
    const personalization = this.getPersonalizationData()

    try {
      if (personalization?.text && personalization.personalizationVariantId && cartContainer?.addJson) {
        await cartContainer.addJson({
          items: this.buildPersonalizedItems(formData, personalization)
        })
      } else {
        if (personalization?.fieldName) {
          formData.delete(personalization.fieldName)
        }

        await cartContainer.add(formData)
      }
    } catch (error) {
      console.error('Unable to add product to cart.', error)
    } finally {
      this.atcBtn.classList.remove('loading')
      this.atcBtn.disabled = false
      this.atcBtn.setAttribute('aria-busy', 'false')
    }
  }

  onClickBuyBtn () {
    if (!this.validateForm()) return

    this.buyBtn.classList.add('loading')
    const variantId = this.getSelectedVariantId()
    const qty = this.getQuantity()
    const personalization = this.getPersonalizationData()
    const cartPath = `${window.Shopify?.routes?.root || '/'}cart`
    const formData = new FormData(this.form)
    this.pruneEmptyProperties(formData)
    const properties = this.getLineItemProperties(formData)
    const propertiesQuery = this.buildPropertiesQuery(properties)

    if (personalization?.text && personalization.personalizationVariantId) {
      window.location.href = `${cartPath}/${variantId}:${qty},${personalization.personalizationVariantId}:${qty}${propertiesQuery}`
      return
    }

    window.location.href = `${cartPath}/${variantId}:${qty}${propertiesQuery}`
  }

  validateForm () {
    if (this.form.reportValidity()) return true

    this.form.classList.add('was-validated')
    this.form.querySelector(':invalid')?.focus()

    return false
  }

  getSelectedVariantId () {
    return this.form.querySelector('[name="id"]')?.value
  }

  getQuantity () {
    const quantity = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1
  }

  getPersonalizationData () {
    const input = this.personalizationInput

    if (!input) return null

    return {
      fieldName: input.name,
      mainProductTitle: input.dataset.mainProductTitle || '',
      personalizationProductTitle: input.dataset.personalizationProductTitle || '',
      personalizationVariantId: input.dataset.personalizationVariantId,
      propertyName: input.dataset.personalizationPropertyName || 'Personalization',
      text: input.value.trim()
    }
  }

  getLineItemProperties (formData) {
    const properties = {}

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)]$/)
      if (!match) return

      const propertyValue = typeof value === 'string' ? value.trim() : value
      if (!propertyValue) return

      properties[match[1]] = propertyValue
    })

    return properties
  }

  pruneEmptyProperties (formData) {
    const emptyPropertyKeys = []

    formData.forEach((value, key) => {
      if (!key.match(/^properties\[(.+)]$/)) return
      if (typeof value === 'string' && value.trim() === '') emptyPropertyKeys.push(key)
    })

    emptyPropertyKeys.forEach(key => formData.delete(key))
  }

  buildPersonalizedItems (formData, personalization) {
    const variantId = this.getSelectedVariantId()
    const qty = this.getQuantity()
    const mainProductLabel = personalization.mainProductTitle
      ? `${personalization.mainProductTitle} (${variantId})`
      : String(variantId)

    return [
      {
        id: Number(variantId),
        quantity: qty,
        properties: this.getLineItemProperties(formData)
      },
      {
        id: Number(personalization.personalizationVariantId),
        quantity: qty,
        properties: {
          _personalization_for: mainProductLabel,
          _personalization_text: personalization.text
        }
      }
    ]
  }

  buildPropertiesQuery (properties) {
    if (!properties || Object.keys(properties).length === 0) return ''

    return `?properties=${this.base64UrlEncode(JSON.stringify(properties))}`
  }

  base64UrlEncode (value) {
    let binary = ''

    if (typeof TextEncoder !== 'undefined') {
      const bytes = new TextEncoder().encode(value)
      bytes.forEach(byte => { binary += String.fromCharCode(byte) })
    } else {
      binary = unescape(encodeURIComponent(value))
    }

    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  }
}
customElements.define('product-form', ProductForm)

class GiftCardRecipientForm extends HTMLElement {
  constructor () {
    super()

    this.toggle = this.querySelector('[data-gift-card-recipient-toggle]')
    this.fieldsContainer = this.querySelector('[data-gift-card-recipient-fields]')
    this.fields = this.querySelectorAll('[data-gift-card-recipient-field]')
    this.requiredFields = this.querySelectorAll('[data-gift-card-recipient-required]')
    this.offsetInput = this.querySelector('[data-gift-card-recipient-offset]')
    this.dateInput = this.querySelector('[data-gift-card-recipient-date]')
    this.messageInput = this.querySelector('[data-gift-card-recipient-message]')
    this.messageCounter = this.querySelector('[data-gift-card-recipient-message-counter]')

    if (!this.toggle) return

    this.toggle.addEventListener('change', () => this.syncFields())
    this.messageInput?.addEventListener('input', () => this.updateMessageCounter())

    this.setDateConstraints()
    this.syncFields()
    this.updateMessageCounter()
  }

  syncFields () {
    const isEnabled = this.toggle.checked

    this.fieldsContainer?.toggleAttribute('hidden', !isEnabled)

    this.fields.forEach(field => {
      field.disabled = !isEnabled
    })

    this.requiredFields.forEach(field => {
      field.required = isEnabled
    })

    this.setOffset()
  }

  setOffset () {
    if (!this.offsetInput) return

    this.offsetInput.value = String(new Date().getTimezoneOffset())
  }

  setDateConstraints () {
    if (!this.dateInput) return

    const today = new Date()
    this.dateInput.min = this.formatDate(today)

    const maxSendOnDays = Number(this.dataset.maxSendOnDays || 0)

    if (Number.isFinite(maxSendOnDays) && maxSendOnDays > 0) {
      const maxDate = new Date(today)
      maxDate.setDate(maxDate.getDate() + maxSendOnDays)
      this.dateInput.max = this.formatDate(maxDate)
    }
  }

  updateMessageCounter () {
    if (!this.messageInput || !this.messageCounter) return

    const maxLength = this.messageInput.maxLength > 0
      ? this.messageInput.maxLength
      : Number(this.dataset.messageMaxLength || 0)

    this.messageCounter.textContent = maxLength
      ? `${this.messageInput.value.length}/${maxLength}`
      : String(this.messageInput.value.length)
  }

  formatDate (date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }
}
customElements.define('gift-card-recipient-form', GiftCardRecipientForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide)

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.listenVariantChange()
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: 'bullets',
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      }
    }
    return options
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.style.top = '20px'
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerGroup.clientHeight + 20}px`
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)
